import React, { useState, useRef, useEffect } from "react";
import EditWhite from "../../assets/svg/edit-white.svg";
import DeleteWhite from "../../assets/svg/delete-white.svg";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Skeleton,
} from "@mui/material";
import ZoomIn from "@mui/icons-material/ZoomIn";
import ZoomOut from "@mui/icons-material/ZoomOut";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { readFirstSheetAoA } from "../../utils/xlsxHelpers";
import showToast from "../Toast/Toster";
import { Button } from "reactstrap";
import { Grid } from "@mui/material";
import { Row, Col } from "react-bootstrap";
import { SpinnerDotted } from "spinners-react";
import { padding } from "@mui/system";
import "./style.css";
import Tooltip from "@mui/material/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-regular-svg-icons";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import WhiteButton from "../Button/WhiteButton";
import { useUserType } from "../../contexts/UserTypeContext";
import { useLocation } from "react-router-dom";
import {
  AdminPageSkeleton,
  AdminTableCardSkeleton,
  AdminDetailPageSkeleton,
  AdminFormBlockSkeleton,
} from "../Loading/AdminDataSkeleton";
import { useAdminDarkMode } from "../../contexts/AdminDarkModeContext";

/** Donor portal / donor company / donor URLs → spinners only; everyone else → skeleton by default */
function useLoaderResolvedVariant(variant, isDonationMode) {
  const { userType, isDonor } = useUserType();
  const { pathname = "" } = useLocation();
  const donorLike =
    isDonationMode === true ||
    isDonor === true ||
    userType === "donor" ||
    pathname.includes("/donors") ||
    pathname.includes("/donordetails");
  if (donorLike) return "spinner";
  if (variant === "spinner") return "spinner";
  return "skeleton";
}

const EditIcon = ({
  onClick,
  color = "#0f2b46",
  style,
  className,
  disableTooltip,
  ...props
}) => {
  const mergedStyle = {
    cursor: onClick ? "pointer" : "default",
    marginRight: "7px",
    color,
    ...style,
  };

  const icon = (
    <FontAwesomeIcon
      icon={faPenToSquare}
      alt="edit"
      onClick={onClick}
      style={mergedStyle}
      className={`margin-rightNone edit-Icon ${className || ""}`}
      {...props}
    />
  );

  if (disableTooltip) return icon;

  return (
    <Tooltip title="Edit" arrow>
      {icon}
    </Tooltip>
  );
};

const EditIconWhite = ({ onClick }) => {
  return (
    <div
      src={EditWhite}
      alt="edit"
      style={{ cursor: "pointer" }}
      onClick={onClick}
      className="EditWhite-Icon"
    ></div>
  );
};
const DeleteIcone = ({
  onClick,
  color = "#0f2b46",
  style,
  className,
  disabled,
  disabledTitle = "You don't have permission to delete this.",
  disableTooltip,
  ...props
}) => {
  const mergedStyle = {
    cursor: disabled ? "not-allowed" : onClick ? "pointer" : "default",
    color,
    ...style,
  };
  const title = disabled ? disabledTitle : "Delete";
  const inner = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 0,
        cursor: disabled ? "not-allowed" : onClick ? "pointer" : "default",
      }}
    >
      <FontAwesomeIcon
        icon={faTrashCan}
        alt="delete"
        style={mergedStyle}
        onClick={disabled ? undefined : onClick}
        className={`margin-rightNone edit-Icon ${className || ""}`}
        {...props}
      />
    </span>
  );

  if (disableTooltip) return inner;

  return (
    <Tooltip title={title} arrow>
      {inner}
    </Tooltip>
  );
};
const DeleteIconeWhite = ({ onClick }) => {
  return (
    <div
      src={DeleteWhite}
      alt="edit"
      style={{ cursor: "pointer" }}
      onClick={onClick}
      className="margin-rightNone DeleteWhite-Icon"
    />
  );
};
const LoaderComponent = ({
  loader,
  height,
  width,
  isDonationMode,
  /** `"spinner"` forces dots. Omit or `"skeleton"` → shimmer except donor/donor-company (see useLoaderResolvedVariant). */
  variant,
  /**
   * When showing skeleton (non-tiny): `page` = generic toolbar + rows;
   * `detail` = customer/quote/invoice-style header + tabs + body;
   * `table` = table row bars (JobberListCard default; use for inline full-width table load).
   * `form` = profile / account-style fields only (no full-viewport fill).
   */
  skeletonLayout = "page",
  /** Optional min height applied to `skeletonLayout="page"` skeleton. */
  skeletonMinHeight,
}) => {
  const resolvedVariant = useLoaderResolvedVariant(variant, isDonationMode);
  if (!loader) return null;
  if (resolvedVariant === "skeleton") {
    const h = Number(height);
    const w = Number(width);
    const isTiny =
      (Number.isFinite(h) && h <= 32) || (Number.isFinite(w) && w <= 32);
    if (isTiny) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            minHeight: 40,
            width: "100%",
          }}
        >
          <Skeleton
            variant="rounded"
            animation="wave"
            width={120}
            height={36}
            sx={{ borderRadius: "8px" }}
          />
        </Box>
      );
    }
    if (skeletonLayout === "detail") {
      return <AdminDetailPageSkeleton />;
    }
    if (skeletonLayout === "table") {
      return <AdminTableCardSkeleton rows={12} />;
    }
    if (skeletonLayout === "form") {
      return <AdminFormBlockSkeleton />;
    }
    return <AdminPageSkeleton minHeight={skeletonMinHeight || 0} />;
  }
  return (
    <SpinnerDotted
      height={height}
      width={width}
      thickness={180}
      speed={130}
      color={isDonationMode ? "#68b2cb" : "#0f2b46"}
      padding={padding}
      visible={loader}
    />
  );
};

const WhiteLoaderComponent = ({ loader, height, width }) => {
  return (
    <Grid
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <SpinnerDotted
        height={height}
        width={width}
        thickness={180}
        speed={130}
        color="#ffff"
        padding={padding}
        visible={loader}
      />
    </Grid>
  );
};
const CustomCheckbox = ({ id, name, checked, onChange, error, label }) => {
  return (
    <FormControlLabel
      control={
        <Checkbox
          id={id}
          name={name}
          checked={checked}
          onChange={onChange}
          size="small"
          sx={{
            color: error ? "error.main" : undefined,
            py: 0,
          }}
        />
      }
      label={label}
      className="text-blue-color"
      sx={{
        alignItems: "flex-start",
        gap: 0.5,
        m: 0,
        "& .MuiFormControlLabel-label": {
          cursor: "pointer",
          color: error ? "error.main" : "inherit",
          fontWeight: error ? "bold" : "normal",
          fontSize: "14px",
        },
      }}
    />
  );
};

const ProductItem = ({
  item,
  index,
  baseUrl,
  onImageClick,
  quoteDetailV2,
  quoteDetailAtlas,
}) => {
  const theme = useTheme();
  const { darkMode: adminDark } = useAdminDarkMode();
  const isLegacyLineItemsDark = !quoteDetailV2 && theme.palette.mode === "dark";
  const qdV2Dark =
    quoteDetailV2 && (adminDark || theme.palette.mode === "dark");
  /* Cloud Job Manager style guide palette — quote detail v2 only */
  const sg = quoteDetailV2
    ? qdV2Dark
      ? {
          navyDeep: "#e8eaed",
          border: "#2d3b4e",
          borderDark: "#3d4f66",
          blue50: "#1a2332",
          bgMain: "#151d2a",
          textMuted: "#9aa0a6",
        }
      : {
          navyDeep: "#0F2B46",
          border: "#E5E7EB",
          borderDark: "#D1D5DB",
          blue50: "#EFF6FF",
          bgMain: "#F7F8FA",
          textMuted: "#6B7280",
        }
    : null;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isSmallDesktop = useMediaQuery(theme.breakpoints.between("lg", "xl"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const qdV2RowBorder = quoteDetailV2
    ? qdV2Dark
      ? "1px solid #2d3b4e"
      : "1px solid #E5E7EB"
    : "none";
  const qdV2ImageNoteColor = quoteDetailV2
    ? qdV2Dark
      ? "#9aa0a6"
      : "#0F2B46"
    : isLegacyLineItemsDark
      ? "#94a3b8"
      : "#063164";

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState("");
  const [imageZoom, setImageZoom] = useState(100);
  const [modalWidth, setModalWidth] = useState("sm");
  const [previewContent, setPreviewContent] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const [previewRowsPerPage, setPreviewRowsPerPage] = useState(10);
  const [fileType, setFileType] = useState(null);
  const imgRef = useRef(null);
  
  // CSV parsing function
  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
    return lines.map((line) => {
      const result = [];
      let current = "";
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];
        
        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === "," && !inQuotes) {
          result.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    });
  };

  // Check if file is CSV or Excel
  const isCSVFile = (fileName) => {
    if (typeof fileName === "string") {
      const name = fileName.split("?")[0].toLowerCase(); // Remove query params
      return name.endsWith(".csv");
    }
    return false;
  };

  const isExcelFile = (fileName) => {
    if (typeof fileName === "string") {
      const name = fileName.split("?")[0].toLowerCase(); // Remove query params
      return name.endsWith(".xls") || name.endsWith(".xlsx") || name.endsWith(".excel");
    }
    return false;
  };

  const isTxtFile = (fileName) => {
    if (typeof fileName === "string") {
      const name = fileName.split("?")[0].toLowerCase(); // Remove query params
      return name.endsWith(".txt");
    }
    return false;
  };

  const isPdfFile = (fileName) => {
    if (typeof fileName === "string") {
      const name = fileName.split("?")[0].toLowerCase(); // Remove query params
      return name.endsWith(".pdf");
    }
    return false;
  };

  const isImageFile = (fileName) => {
    if (typeof fileName === "string") {
      const name = fileName.split("?")[0].toLowerCase(); // Remove query params
      return name.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i);
    }
    return false;
  };

  const openFileModal = async (fileName) => {
    if (onImageClick) {
      // Use external zoom handler if provided
      onImageClick(`${baseUrl}/v1/cdn/upload/${fileName}`);
    } else {
      // Detect file type
      const fileUrl = `${baseUrl}/v1/cdn/upload/${fileName}`;
      setFile(fileUrl);
      
      // Determine file type
      if (isPdfFile(fileName)) {
        setFileType("pdf");
        setOpenPdf(true);
        setOpen(false);
      } else if (isCSVFile(fileName)) {
        setFileType("csv");
        setOpen(true);
        setOpenPdf(false);
        setImageZoom(100);
        setModalWidth("lg");
        await parseAndPreviewFile(fileUrl, "csv");
      } else if (isExcelFile(fileName)) {
        setFileType("excel");
        setOpen(true);
        setOpenPdf(false);
        setImageZoom(100);
        setModalWidth("lg");
        await parseAndPreviewFile(fileUrl, "excel");
      } else if (isTxtFile(fileName)) {
        setFileType("txt");
        setOpen(true);
        setOpenPdf(false);
        setImageZoom(100);
        setModalWidth("sm");
      } else if (isImageFile(fileName)) {
        setFileType("image");
        setOpen(true);
        setOpenPdf(false);
        setImageZoom(100);
        setModalWidth("sm");
      } else {
        setFileType("other");
        setOpen(true);
        setOpenPdf(false);
        setImageZoom(100);
        setModalWidth("sm");
      }
    }
  };

  const parseAndPreviewFile = async (fileUrl, type) => {
    setIsLoadingPreview(true);
    setPreviewContent(null);
    setPreviewPage(0);
    setPreviewRowsPerPage(10);
    
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }
      const blob = await response.blob();

      if (type === "csv") {
        const text = await blob.text();
        const rows = parseCSV(text);
        if (rows.length > 0) {
          setPreviewContent({ type: "csv", data: rows });
        } else {
          throw new Error("CSV file is empty");
        }
      } else if (type === "excel") {
        const arrayBuffer = await blob.arrayBuffer();
        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          throw new Error("Excel file is empty or invalid");
        }
        const data = await readFirstSheetAoA(arrayBuffer);
        if (!data || data.length === 0) {
          throw new Error("Excel file is empty or has no sheets");
        }
        setPreviewContent({ type: "excel", data: data });
      }
    } catch (error) {
      console.error("Error parsing file:", error);
      showToast.error(`Failed to preview file: ${error.message}`);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handlePreviewPageChange = (event, newPage) => {
    setPreviewPage(newPage);
  };

  const handlePreviewRowsPerPageChange = (event) => {
    setPreviewRowsPerPage(parseInt(event.target.value, 10));
    setPreviewPage(0);
  };

  const getPreviewData = () => {
    if (!previewContent?.data) return [];
    const dataWithoutHeader = previewContent.data.slice(1);
    const startIndex = previewPage * previewRowsPerPage;
    return dataWithoutHeader.slice(startIndex, startIndex + previewRowsPerPage);
  };

  const getTotalRows = () => {
    if (!previewContent?.data?.length) return 0;
    return Math.max(0, previewContent.data.length - 1);
  };
  const [openPdf, setOpenPdf] = useState(false);
  const [filePdf, setFilePdf] = useState("");
  const openFileModalModel = (fileName) => {
    setFilePdf(`${baseUrl}/v1/cdn/upload/${fileName}`);
    setOpenPdf(true);
  };

  const closeFileModal = () => {
    setOpen(false);
    setFile("");
    setOpenPdf(false);
    setFilePdf("");
    setImageZoom(100);
    setModalWidth("sm");
    setPreviewContent(null);
    setFileType(null);
    setPreviewPage(0);
    setPreviewRowsPerPage(10);
    setIsLoadingPreview(false);
  };

  const updateImageSize = () => {
    if (imgRef.current && file && open) {
      const img = imgRef.current;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      
      if (naturalWidth > 0 && naturalHeight > 0) {
        const containerWidth = img.parentElement?.clientWidth || 560;
        const padding = 40;
        const availableWidth = containerWidth - padding;
        
        // Calculate image width based on zoom
        let imageWidth;
        if (imageZoom === 100) {
          imageWidth = Math.min(naturalWidth, availableWidth);
          img.style.width = "auto";
          img.style.maxWidth = "100%";
          img.style.height = "auto";
          img.style.maxHeight = "none";
        } else {
          const displayWidthAt100 = Math.min(naturalWidth, availableWidth);
          imageWidth = (displayWidthAt100 * imageZoom) / 100;
          img.style.width = `${imageWidth}px`;
          img.style.height = "auto";
          img.style.maxWidth = "none";
          img.style.maxHeight = "none";
        }
        
        // Adjust modal width based on image size and zoom
        const totalWidth = imageWidth + padding + 80;
        if (totalWidth < 500) {
          setModalWidth("xs");
        } else if (totalWidth < 700) {
          setModalWidth("sm");
        } else if (totalWidth < 900) {
          setModalWidth("md");
        } else {
          setModalWidth("lg");
        }
      }
    }
  };

  useEffect(() => {
    updateImageSize();
  }, [imageZoom, file, open]);

  const handleZoomIn = () => {
    setImageZoom((prev) => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setImageZoom((prev) => Math.max(prev - 25, 50));
  };

  const handleResetZoom = () => {
    setImageZoom(100);
  };

  const handleDownload = async () => {
    const fileUrl = openPdf ? filePdf : file;
    if (fileUrl) {
      try {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileUrl.split("/").pop() || "attachment";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      } catch (error) {
        console.error("Error downloading file:", error);
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = fileUrl.split("/").pop() || "attachment";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      }
    }
  };

  return (
    <Box
      className={quoteDetailAtlas ? "qdj-pi-atlas" : undefined}
      sx={{ width: "100%" }}
    >
      {/* Desktop & Tablet Layout */}
      {index === 0 && (
        <Box
          className="mb-3"
          sx={{
            backgroundColor: quoteDetailV2
              ? "transparent"
              : isLegacyLineItemsDark
                ? "#1a2332"
                : "#F9F4F2",
            borderRadius: quoteDetailV2 ? 0 : "6px",
            borderBottom: qdV2RowBorder,
            marginBottom: quoteDetailV2
              ? { md: "8px", lg: "10px" }
              : { md: "12px", lg: "16px" },
            width: "100%",
            display: { xs: "none", lg: "flex" },
            overflow: "hidden",
            padding: quoteDetailV2 ? "8px 0 12px" : "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
              flex: 1,
              minWidth: 0,
              flexWrap: quoteDetailV2 ? "nowrap" : undefined,
              alignItems: quoteDetailV2 ? "center" : undefined,
            }}
          >
            <Box
              sx={{
                flex: "0 0 33.333333%",
                maxWidth: "33.333333%",
                minWidth: 0,
                paddingLeft: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
                paddingRight: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
              }}
            >
              <Typography
                className="productServiceHead text-blue-color"
                sx={{
                  fontSize: { md: "15px", lg: "16px", xl: "16px" },
                  fontWeight: 600,
                  whiteSpace: quoteDetailV2 ? "nowrap" : undefined,
                }}
              >
                Materials & Labor
              </Typography>
            </Box>
            <Box
              sx={{
                flex: "0 0 16.666667%",
                maxWidth: "16.666667%",
                minWidth: 0,
                paddingLeft: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
                paddingRight: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
                textAlign: quoteDetailV2 ? "left" : "right",
              }}
            >
              <Typography
                className="productServiceHead text-blue-color"
                sx={{
                  fontSize: isTablet ? "15px" : "16px",
                  fontWeight: 600,
                  whiteSpace: quoteDetailV2 ? "nowrap" : undefined,
                }}
              >
                Image
              </Typography>
            </Box>
            <Box
              sx={{
                flex: "0 0 16.666667%",
                maxWidth: "16.666667%",
                minWidth: 0,
                paddingLeft: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
                paddingRight: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
                textAlign: quoteDetailV2 ? "left" : "right",
              }}
            >
              <Typography
                className="productServiceHead text-blue-color"
                sx={{
                  fontSize: isTablet ? "15px" : "16px",
                  fontWeight: 600,
                  whiteSpace: quoteDetailV2 ? "nowrap" : undefined,
                }}
              >
                Measurement
              </Typography>
            </Box>
            <Box
              sx={{
                flex: "0 0 16.666667%",
                maxWidth: "16.666667%",
                minWidth: 0,
                paddingLeft: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
                paddingRight: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
                textAlign: "right",
              }}
            >
              <Typography
                className="productServiceHead text-blue-color"
                sx={{
                  fontSize: isTablet ? "15px" : "16px",
                  fontWeight: 600,
                  whiteSpace: quoteDetailV2 ? "nowrap" : undefined,
                }}
              >
                Cost
              </Typography>
            </Box>
            <Box
              sx={{
                flex: "0 0 16.666667%",
                maxWidth: "16.666667%",
                minWidth: 0,
                paddingLeft: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
                paddingRight: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
                textAlign: "right",
              }}
            >
              <Typography
                className="productServiceHead text-blue-color totalInput totalPrice_contract totalOfQuoteDes"
                sx={{
                  fontSize: isTablet ? "15px" : "16px",
                  fontWeight: 600,
                  whiteSpace: quoteDetailV2 ? "nowrap" : undefined,
                }}
              >
                Total
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      <Box
        className="mb-3"
        sx={{
          width: "100%",
          display: { xs: "none", lg: "flex" },
          padding: quoteDetailV2 ? "12px 0 20px" : "10px",
          borderBottom: qdV2RowBorder,
          marginBottom: quoteDetailV2 ? 0 : undefined,
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: "0 0 33.333333%",
              maxWidth: "33.333333%",
              minWidth: 0,
              paddingLeft: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
              paddingRight: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
            }}
          >
            <Grid className="d-flex justify-content-between products mb-2">
              <Grid className="mb-2">
                <Typography
                  className="mb-0 Sub-title text-blue-color"
                  sx={{
                    fontSize: { md: "13px", lg: "14px", xl: "14px" },
                    fontWeight: 600,
                    marginBottom: { md: "4px", lg: "6px" }
                  }}
                >
                  {item?.Name}
                </Typography>
                <Typography
                  className="mb-0 text-data text-blue-color"
                  sx={{
                    fontSize: { md: "12px", lg: "13px", xl: "14px" },
                    whiteSpace: "break-spaces",
                    lineHeight: { md: "1.4", lg: "1.5" },
                    wordBreak: "break-word"
                  }}
                >
                  {item?.Description ||
                    "Labor charge for the setup and configuration of network switches for improved connectivity and network management."}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              flex: "0 0 16.666667%",
              maxWidth: "16.666667%",
              minWidth: 0,
              paddingLeft: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
              paddingRight: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
              textAlign: quoteDetailV2 ? "left" : "right",
            }}
          >
            <Typography
              className="mb-0 text-data text-blue-color unit"
              sx={{
                fontSize: { md: "12px", lg: "13px", xl: "14px" },
              }}
            >
              {item?.Attachment ? (
                isImageFile(item?.Attachment) ? (
                  <img
                    style={{
                      marginBottom: isTablet ? "20px" : "30px",
                      cursor: "pointer",
                      height: isTablet ? "35px" : isSmallDesktop ? "40px" : "45px",
                      maxWidth: "100%",
                      objectFit: "contain",
                      ...(quoteDetailV2 ? { display: "block" } : {}),
                    }}
                    src={`${baseUrl}/v1/cdn/upload/${item?.Attachment}`}
                    onClick={() => openFileModal(item?.Attachment)}
                    alt="Image Thumbnail"
                  />
                ) : (
                  <i style={{ 
                    fontSize: isTablet ? "12px" : "14px", 
                    color: qdV2ImageNoteColor,
                    display: "block",
                    textAlign: quoteDetailV2 ? "left" : "right"
                  }}>
                    No image provided
                  </i>
                )
              ) : (
                <i style={{ 
                  fontSize: isTablet ? "12px" : "14px", 
                  color: qdV2ImageNoteColor,
                  display: "block",
                  textAlign: quoteDetailV2 ? "left" : "right"
                }}>
                  No image provided
                </i>
              )}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: "0 0 16.666667%",
              maxWidth: "16.666667%",
              minWidth: 0,
              paddingLeft: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
              paddingRight: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
              textAlign: quoteDetailV2 ? "left" : "right",
            }}
          >
            <Typography
              className="mb-0 text-data text-blue-color unit"
              sx={{
                fontSize: { md: "12px", lg: "13px", xl: "14px" },
                wordBreak: "break-word",
                textAlign: quoteDetailV2 ? "left" : undefined,
              }}
            >
              {(() => {
                let displayValue = "";
                // Check cost type first to correctly identify Sq. Ft. vs Unit
                if (item?.CostPerHour && item?.Hourly) {
                  displayValue = `Hourly : ${item?.Hourly}`;
                } else if (item?.CostPerSquare && item?.Unit) {
                  displayValue = `Sq. Ft. : ${item?.Unit}`;
                } else if (item?.CostPerSquare && item?.Square) {
                  displayValue = `Sq. Ft. : ${item?.Square}`;
                } else if (item?.CostPerUnit && item?.Unit) {
                  displayValue = `Unit : ${item?.Unit}`;
                } else if (item?.CostPerFixed && item?.Fixed) {
                  displayValue = `Fixed : ${item?.Fixed}`;
                } else if (item?.Hourly) {
                  displayValue = `Hourly : ${item?.Hourly}`;
                } else if (item?.Square) {
                  displayValue = `Sq. Ft. : ${item?.Square}`;
                } else if (item?.Unit) {
                  displayValue = `Unit : ${item?.Unit}`;
                } else if (item?.Fixed) {
                  displayValue = `Fixed : ${item?.Fixed}`;
                }
                return displayValue;
              })()}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: "0 0 16.666667%",
              maxWidth: "16.666667%",
              minWidth: 0,
              paddingLeft: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
              paddingRight: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
              textAlign: "right",
            }}
          >
            <Typography
              className="mb-0 text-data text-blue-color per-unit"
              sx={{
                fontSize: { md: "12px", lg: "13px", xl: "14px" },
              }}
            >
              $
              {item?.CostPerHour
                ? `${new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(item?.CostPerHour)}`
                : item?.CostPerSquare
                ? `${new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(item?.CostPerSquare)}`
                : item?.CostPerUnit
                ? `${new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(item?.CostPerUnit)}`
                : item?.CostPerFixed
                ? `${new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(item?.CostPerFixed)}`
                : "-"}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: "0 0 16.666667%",
              maxWidth: "16.666667%",
              minWidth: 0,
              paddingLeft: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
              paddingRight: isTablet ? "8px" : isSmallDesktop ? "12px" : "16px",
              textAlign: "right",
            }}
          >
            <Typography
              className="mb-0 text-data text-blue-color total-pro"
              sx={{ 
                fontSize: { md: "12px", lg: "13px", xl: "14px" },
              }}
            >
              {`$${
                item?.Total
                  ? new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(item?.Total)
                  : "-"
              }`}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Mobile / tablet card layout (viewports below lg — avoids cramped multi-column table) */}
      <Box
        sx={{
          border: quoteDetailV2
            ? "none"
            : isLegacyLineItemsDark
              ? "1px solid #2d3b4e"
              : "1px solid #e0e0e0",
          borderRadius: quoteDetailV2 ? 0 : { xs: "6px", sm: "8px" },
          borderBottom: quoteDetailV2 ? qdV2RowBorder : undefined,
          padding: quoteDetailV2
            ? { xs: "16px 0", sm: "18px 0", md: "20px 0" }
            : { xs: "14px", sm: "16px", md: "18px" },
          marginBottom: quoteDetailV2
            ? { xs: "4px", sm: "6px", md: "8px" }
            : { xs: "12px", sm: "14px", md: "16px" },
          backgroundColor: quoteDetailV2
            ? "transparent"
            : isLegacyLineItemsDark
              ? "#151d2a"
              : "#fff",
          boxShadow: quoteDetailV2 ? "none" : "0 2px 4px rgba(0,0,0,0.1)",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          display: { xs: "block", lg: "none" },
          overflow: "hidden",
        }}
      >
        {/* Header — left-aligned with body (matches labels / description) */}
        {index === 0 && (
          <Typography
            className="mb-3 productServiceHead text-blue-color"
            sx={{
              fontSize: { xs: "16px", sm: "18px" },
              fontWeight: 600,
              marginBottom: { xs: "12px", sm: "16px" },
              textAlign: "left",
            }}
          >
            Materials & Labor
          </Typography>
        )}

        {/* Product Name */}
        <Box sx={{ marginBottom: { xs: "10px", sm: "12px" } }}>
          <Typography
            className="text-blue-color"
            sx={{
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 600,
              marginBottom: "4px",
            }}
          >
            {item?.Name}
          </Typography>
          <Typography
            className="text-blue-color"
            sx={{
              fontSize: { xs: "12px", sm: "13px", md: "14px" },
              lineHeight: "1.4",
              color: sg?.textMuted ?? "#666",
              wordBreak: "break-word",
            }}
          >
            {item?.Description ||
              "Labor charge for the setup and configuration of network switches for improved connectivity and network management."}
          </Typography>
        </Box>

        {/* Details Grid — align columns to start; image row is left-aligned (not centered) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: { xs: "14px", sm: "16px" },
            marginBottom: 0,
            paddingBottom: { xs: "14px", sm: "16px" },
            borderBottom: `1px solid ${qdV2Dark ? "#2d3b4e" : sg?.border ?? "#e8ecf1"}`,
            alignItems: "start",
          }}
        >
          {/* Attachment */}
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <Typography
              className="text-blue-color"
              sx={{
                fontSize: { xs: "11px", sm: "12px" },
                fontWeight: 600,
                marginBottom: "6px",
                color: qdV2Dark ? "text.secondary" : sg?.navyDeep ?? "#063164",
                textAlign: "left",
              }}
            >
              Image
            </Typography>
            <Box
              sx={{
                textAlign: "left",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              {item?.Attachment ? (
                isImageFile(item?.Attachment) ? (
                  <Box
                    component="img"
                    sx={{
                      cursor: "pointer",
                      display: "block",
                      width: "100%",
                      maxWidth: { xs: "min(100%, 240px)", sm: 220 },
                      height: { xs: 128, sm: 120 },
                      objectFit: "contain",
                      objectPosition: "left center",
                      borderRadius: "8px",
                      border: sg
                        ? qdV2Dark
                          ? "1px solid rgba(255, 255, 255, 0.12)"
                          : "1px solid rgba(15, 43, 70, 0.12)"
                        : "1px solid rgba(6, 49, 100, 0.12)",
                      backgroundColor: sg?.blue50 ?? "#f1f5f9",
                    }}
                    src={`${baseUrl}/v1/cdn/upload/${item?.Attachment}`}
                    onClick={() => openFileModal(item?.Attachment)}
                    alt="Product image"
                  />
                ) : (
                  <Box
                    sx={{
                      minHeight: { xs: 72, sm: 80 },
                      width: "100%",
                      maxWidth: { xs: 220, sm: 200 },
                      border: `1px dashed ${sg?.borderDark ?? "#ccc"}`,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      px: 1.5,
                      py: 1,
                      boxSizing: "border-box",
                      backgroundColor: sg?.bgMain ?? "#fafafa",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: "12px", sm: "13px" },
                        fontStyle: "italic",
                        color: sg?.textMuted ?? "#64748b",
                        textAlign: "left",
                      }}
                    >
                      No image provided
                    </Typography>
                  </Box>
                )
              ) : (
                <Box
                  sx={{
                    minHeight: { xs: 72, sm: 80 },
                    width: "100%",
                    maxWidth: { xs: 220, sm: 200 },
                    border: `1px dashed ${sg?.borderDark ?? "#ccc"}`,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    px: 1.5,
                    py: 1,
                    boxSizing: "border-box",
                    backgroundColor: sg?.bgMain ?? "#fafafa",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { xs: "12px", sm: "13px" },
                      fontStyle: "italic",
                      color: sg?.textMuted ?? "#64748b",
                      textAlign: "left",
                    }}
                  >
                    No image provided
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Measurement */}
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <Typography
              className="text-blue-color"
              sx={{
                fontSize: { xs: "11px", sm: "12px" },
                fontWeight: 600,
                marginBottom: "6px",
                color: sg?.navyDeep ?? "#0f2b46",
                textAlign: "left",
              }}
            >
              Measurement
            </Typography>
            <Typography
              className="text-blue-color"
              sx={{
                fontSize: { xs: "12px", sm: "13px", md: "14px" },
                lineHeight: 1.45,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                textAlign: "left",
              }}
            >
              {(() => {
                let displayValue = "";
                // Check cost type first to correctly identify Sq. Ft. vs Unit
                if (item?.CostPerHour && item?.Hourly) {
                  displayValue = `Hourly: ${item?.Hourly}`;
                } else if (item?.CostPerSquare && item?.Unit) {
                  displayValue = `Sq. Ft.: ${item?.Unit}`;
                } else if (item?.CostPerSquare && item?.Square) {
                  displayValue = `Sq. Ft.: ${item?.Square}`;
                } else if (item?.CostPerUnit && item?.Unit) {
                  displayValue = `Unit: ${item?.Unit}`;
                } else if (item?.CostPerFixed && item?.Fixed) {
                  displayValue = `Fixed: ${item?.Fixed}`;
                } else if (item?.Hourly) {
                  displayValue = `Hourly: ${item?.Hourly}`;
                } else if (item?.Square) {
                  displayValue = `Sq. Ft.: ${item?.Square}`;
                } else if (item?.Unit) {
                  displayValue = `Unit: ${item?.Unit}`;
                } else if (item?.Fixed) {
                  displayValue = `Fixed: ${item?.Fixed}`;
                }
                return displayValue;
              })()}
            </Typography>
          </Box>
        </Box>

        {/* Cost + Total: two columns on all breakpoints (readable on narrow phones) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: { xs: "10px", sm: "16px" },
            columnGap: { xs: "12px", sm: "24px" },
            marginTop: { xs: "12px", sm: "14px" },
            padding: { xs: "12px", sm: "14px" },
            backgroundColor: sg?.blue50 ?? "#f1f5f9",
            borderRadius: "8px",
            border: sg
              ? "1px solid rgba(15, 43, 70, 0.1)"
              : "1px solid rgba(6, 49, 100, 0.1)",
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ minWidth: 0, textAlign: "left" }}>
            <Typography
              className="text-blue-color"
              component="div"
              sx={{
                fontSize: { xs: "10px", sm: "12px" },
                fontWeight: 700,
                marginBottom: "4px",
                color: sg?.navyDeep ?? "#0f2b46",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Cost
            </Typography>
            <Typography
              className="text-blue-color"
              component="div"
              sx={{
                fontSize: { xs: "0.95rem", sm: "1.05rem" },
                fontWeight: 700,
                lineHeight: 1.25,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              $
              {item?.CostPerHour
                ? `${new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(item?.CostPerHour)}`
                : item?.CostPerSquare
                ? `${new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(item?.CostPerSquare)}`
                : item?.CostPerUnit
                ? `${new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(item?.CostPerUnit)}`
                : item?.CostPerFixed
                ? `${new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(item?.CostPerFixed)}`
                : "-"}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0, textAlign: "right" }}>
            <Typography
              className="text-blue-color"
              component="div"
              sx={{
                fontSize: { xs: "10px", sm: "12px" },
                fontWeight: 700,
                marginBottom: "4px",
                color: sg?.navyDeep ?? "#0f2b46",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Total
            </Typography>
            <Typography
              className="text-blue-color"
              component="div"
              sx={{
                fontSize: { xs: "1rem", sm: "1.125rem" },
                fontWeight: 800,
                color: sg?.navyDeep ?? "#0f2b46",
                lineHeight: 1.25,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {`$${
                item?.Total
                  ? new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(item?.Total)
                  : "-"
              }`}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Responsive Dialog */}
      <Dialog
        open={open || openPdf}
        onClose={closeFileModal}
        /* JOB-3411 follow-up: PDF attachment preview was rendering inside an
           md-sized Dialog (Paper max 800px) with a 600px-tall embed, which on
           a wide screen looked tiny next to the unified 90vw/90vh viewer
           used by Reports and the signed-PDF flows. Promote PDFs to the
           same 90% × 90vh dimensions; other types (image / csv / excel /
           txt) keep their existing per-modal sizing. */
        maxWidth={openPdf ? "lg" : (previewContent?.type === "csv" || previewContent?.type === "excel" ? "lg" : modalWidth)}
        fullWidth={openPdf || previewContent?.type === "csv" || previewContent?.type === "excel"}
        PaperProps={{
          sx: openPdf
            ? {
                width: { xs: "95%", sm: "90%" },
                maxWidth: { xs: "100%", sm: "1200px" },
                margin: { xs: "10px", sm: "20px" },
                height: { xs: "95vh", sm: "90vh" },
                maxHeight: { xs: "95vh", sm: "90vh" },
                borderRadius: "8px",
                boxShadow: "var(--shadow-xl)",
                display: "flex",
                flexDirection: "column",
              }
            : {
                margin: { xs: "0", sm: "16px" },
                width: "auto",
                maxWidth: (previewContent?.type === "csv" || previewContent?.type === "excel" ? "1200px" : (modalWidth === "xs" ? "400px" : modalWidth === "sm" ? "500px" : modalWidth === "md" ? "700px" : "900px")),
                maxHeight: { xs: "100vh", sm: "90vh" },
                borderRadius: "8px",
                boxShadow: "var(--shadow-xl)",
                display: "flex",
                flexDirection: "column",
              }
        }}
      >
        <DialogTitle 
          className="borerBommoModel"
          sx={{
            padding: { xs: "12px 16px", sm: "16px 24px" },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fff",
          }}
        >
          <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Typography 
              className="text-blue-color"
              sx={{
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                fontWeight: 600,
                color: "#0f2b46",
              }}
            >
              Preview
            </Typography>
            {(previewContent?.type === "csv" || previewContent?.type === "excel") && (
              <Chip
                label={previewContent?.type === "csv" ? "CSV" : "EXCEL"}
                style={{
                  backgroundColor: previewContent?.type === "csv" ? "#0f2b46" : "#217346",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "12px",
                  height: "24px",
                }}
              />
            )}
          </Box>
          {open && !openPdf && fileType === "image" && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <IconButton
                onClick={handleZoomOut}
                disabled={imageZoom <= 50}
                size="small"
                sx={{
                  color: imageZoom <= 50 ? "#ccc" : "#0f2b46",
                  "&:disabled": { opacity: 0.5 },
                }}
                title="Zoom Out"
              >
                <ZoomOut />
              </IconButton>
              <Typography
                sx={{
                  minWidth: "60px",
                  textAlign: "center",
                  fontSize: "14px",
                  color: "#0f2b46",
                }}
              >
                {imageZoom}%
              </Typography>
              <IconButton
                onClick={handleZoomIn}
                disabled={imageZoom >= 300}
                size="small"
                sx={{
                  color: imageZoom >= 300 ? "#ccc" : "#0f2b46",
                  "&:disabled": { opacity: 0.5 },
                }}
                title="Zoom In"
              >
                <ZoomIn />
              </IconButton>
              {imageZoom !== 100 && (
                <Button
                  onClick={handleResetZoom}
                  size="small"
                  sx={{
                    marginLeft: 1,
                    fontSize: "12px",
                    color: "#0f2b46",
                    textTransform: "none",
                  }}
                >
                  Reset
                </Button>
              )}
              <IconButton
                onClick={handleDownload}
                size="small"
                sx={{ 
                  color: "#0f2b46",
                  marginLeft: 1,
                }}
                title="Download"
              >
                <FileDownloadOutlined />
              </IconButton>
              <IconButton
                onClick={closeFileModal}
                size="small"
                sx={{ color: "#0f2b46" }}
                title="Close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}
          {(openPdf || (open && fileType !== "image" && fileType !== "csv" && fileType !== "excel")) && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <IconButton
                onClick={handleDownload}
                size="small"
                sx={{ 
                  color: "#0f2b46",
                }}
                title="Download"
              >
                <FileDownloadOutlined />
              </IconButton>
              <IconButton
                onClick={closeFileModal}
                size="small"
                sx={{ color: "#0f2b46" }}
                title="Close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}
          {open && (fileType === "csv" || fileType === "excel") && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <IconButton
                onClick={handleDownload}
                size="small"
                sx={{ 
                  color: "#0f2b46",
                }}
                title="Download"
              >
                <FileDownloadOutlined />
              </IconButton>
              <IconButton
                onClick={closeFileModal}
                size="small"
                sx={{ color: "#0f2b46" }}
                title="Close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}
        </DialogTitle>
        <DialogContent
          sx={{
            textAlign: "center",
            padding: "0",
            overflow: "hidden",
            /* JOB-3411 follow-up: drop the hard-coded light-grey backdrop so
               the PDF iframe sits flush against the dialog and matches the
               dark theme when active. Other content types still render
               inside this container; they don't need a backdrop. */
            backgroundColor: "transparent",
            position: "relative",
            flex: "1 1 auto",
            minHeight: 0,
            maxHeight: { xs: "calc(100vh - 140px)", sm: "calc(90vh - 140px)" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          {isLoadingPreview ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              minHeight={400}
            >
              <CircularProgress />
            </Box>
          ) : (previewContent?.type === "csv" || previewContent?.type === "excel") ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: "calc(90vh - 220px)",
                  overflow: "auto",
                  borderRadius: 0,
                  "& .MuiTable-root": {
                    borderCollapse: "separate",
                    borderSpacing: 0,
                  },
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {previewContent?.data?.[0]?.map((header, index) => (
                        <TableCell
                          key={index}
                          className="bg-blue-color"
                          sx={{
                            textAlign: "left",
                            color: "white",
                            fontWeight: "bold",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            padding: { xs: "8px 12px", sm: "12px 16px" },
                            backgroundColor: "#2c3e50",
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            "&:not(:last-child)": {
                              borderRight: "1px solid rgba(224, 224, 224, 0.4)",
                            },
                          }}
                          title={header}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getPreviewData().map((row, rowIndex) => (
                      <TableRow
                        key={rowIndex}
                        sx={{
                          "&:nth-of-type(even)": {
                            backgroundColor: "#f9f9f9",
                          },
                          "&:hover": {
                            backgroundColor: "#f0f0f0",
                          },
                        }}
                      >
                        {row.map((cell, cellIndex) => (
                          <TableCell
                            key={cellIndex}
                            sx={{
                              padding: { xs: "8px 12px", sm: "12px 16px" },
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              borderRight: "1px solid rgba(224, 224, 224, 0.2)",
                            }}
                          >
                            {cell || ""}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={getTotalRows()}
                page={previewPage}
                onPageChange={handlePreviewPageChange}
                rowsPerPage={previewRowsPerPage}
                onRowsPerPageChange={handlePreviewRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  borderTop: "1px solid #e0e0e0",
                  backgroundColor: "white",
                }}
              />
            </Box>
          ) : openPdf ? (
            /* JOB-3411 follow-up: fill the parent flex container instead of
               the prior fixed 600px / 500px / calc-based heights so the
               PDF expands to the 90vh unified viewer size. */
            <embed
              src={filePdf}
              style={{
                width: "100%",
                height: "100%",
                flex: "1 1 auto",
                minHeight: 0,
                border: "none",
                display: "block",
              }}
              title="PDF Preview"
              type="application/pdf"
            />
          ) : open && fileType === "txt" ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "40vh",
                padding: "40px",
              }}
            >
              <InsertDriveFileIcon
                sx={{
                  width: "80px",
                  height: "80px",
                  color: "#0f2b46",
                  marginBottom: "20px",
                }}
              />
              <Typography variant="h6" sx={{ marginBottom: "10px", fontWeight: 600 }}>
                {file?.split("/").pop() || "Text File"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", textAlign: "center" }}>
                Text files cannot be previewed in the browser. Please download to view.
              </Typography>
            </Box>
          ) : open && fileType === "image" ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "40vh",
                padding: "20px",
                width: "100%",
                boxSizing: "border-box",
                overflow: "auto",
              }}
            >
              <img
                ref={imgRef}
                src={file}
                alt="Preview"
                onLoad={updateImageSize}
                style={{
                  width: "auto",
                  maxWidth: imageZoom === 100 ? "100%" : "none",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "var(--shadow-md)",
                  objectFit: "contain",
                  transition: "width 0.3s ease",
                  display: "block",
                }}
                draggable={false}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/assets/images/no-image.png";
                }}
              />
            </Box>
          ) : open && (fileType === "excel" || fileType === "csv") && !previewContent ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "40vh",
                padding: "40px",
              }}
            >
              <DescriptionIcon
                sx={{
                  width: "80px",
                  height: "80px",
                  color: fileType === "csv" ? "#0f2b46" : "#217346",
                  marginBottom: "20px",
                }}
              />
              <Typography variant="h6" sx={{ marginBottom: "10px", fontWeight: 600 }}>
                {file?.split("/").pop() || `${fileType === "csv" ? "CSV" : "Excel"} File`}
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", textAlign: "center" }}>
                {fileType === "csv" ? "CSV" : "Excel"} files cannot be previewed in the browser. Please download to view.
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions 
          sx={{ 
            padding: { xs: "8px 16px", sm: "12px 24px" },
            borderTop: "1px solid #e0e0e0"
          }}
        >
          <WhiteButton onClick={closeFileModal} label="Close" />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ProductListContainer = ({ items, baseUrl }) => {
  return (
    <Grid className="items-contract">
      {/* Desktop Layout - Single Scrollable Container */}
      <Grid style={{ width: "100%" }} className="ScrollDiv d-none d-md-block">
        <div className="productAndServiceScroll">
          {items.map((item, index) => (
            <ProductItem key={index} item={item} index={index} baseUrl={baseUrl} />
          ))}
        </div>
      </Grid>

      {/* Mobile Layout - Single Scrollable Container */}
      <Grid className="d-block d-md-none">
        {items.map((item, index) => (
          <ProductItem key={index} item={item} index={index} baseUrl={baseUrl} />
        ))}
      </Grid>
    </Grid>
  );
};

const OrangeButton = ({ className = "", label, isDonationMode, ...props }) => {
  return (
    <Button
      {...props}
      className={`text-capitalize text-white-color backgroundBlurBtn ${className}   ${
        isDonationMode
          ? "border-donor-color bg-donor-color"
          : "border-blue-color bg-blue-color blue-button blue-button--contractor"
      }`}
    >
      {label}
    </Button>
  );
};

export {
  EditIcon,
  DeleteIcone,
  DeleteIconeWhite,
  EditIconWhite,
  CustomCheckbox,
  ProductItem,
  ProductListContainer,
  LoaderComponent,
  WhiteLoaderComponent,
  OrangeButton,
};
